import { useState } from 'react';
import './FundSPAdvancedCriteriaPopup.css';
import '../../../Common/typography.css';
import { Box, Button, IconButton } from '@mui/material';
import { ReactComponent as ArrowLeftIcon } from '../../../../assets/icons/arrow-left-s-line.svg';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down-s-line.svg';
import {
  AssetTypeItem,
  ClassificationItem,
  FundCriteria,
  FundCriteriaSetter,
} from '../../../../types/fundScreener.type';
import SingleSelectCriterion from '../SingleSelectCriterion/SingleSelectCriterion';
import FundCriteriaSelect from '../FundCriteriaSelect/FundCriteriaSelect';
import FundCriteriaDetailList from '../FundCriteriaDetailList/FundCriteriaDetailList';

type Screen = 'home' | 'assetType' | 'classification' | 'selectCriteria';

// MUI Button's own compound classes outrank a plain `.body-m-bold` class,
// so typography is re-asserted via `sx` instead.
const bodyMBoldSx = {
  fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  textTransform: 'none' as const,
};

const backButtonSx = { position: 'absolute' as const, left: '16px' };
const addButtonSx = {
  ...bodyMBoldSx,
  border: '1px solid var(--color-brand)',
  color: 'var(--color-brand)',
};
const textButtonSx = {
  ...bodyMBoldSx,
  backgroundColor: 'transparent',
  color: 'var(--color-brand)',
  boxShadow: 'none',
};
const primaryButtonSx = { ...bodyMBoldSx, color: '#ffffff', boxShadow: 'none' };

export interface FundSPAdvancedCriteriaPopupProps {
  themeColor: string;
  market: string;
  assetTypes: AssetTypeItem[];
  setAssetTypes: (items: AssetTypeItem[]) => void;
  classifications: ClassificationItem[];
  setClassifications: (items: ClassificationItem[]) => void;
  tempCriteria: FundCriteria[];
  setTempCriteria: FundCriteriaSetter;
  setOpenSPAdvancedCriteria: (open: boolean) => void;
  onGetResults: () => void;
  onCancel: () => void;
  token: string;
}

const FundSPAdvancedCriteriaPopup = ({
  themeColor,
  market,
  assetTypes,
  setAssetTypes,
  classifications,
  setClassifications,
  tempCriteria,
  setTempCriteria,
  setOpenSPAdvancedCriteria,
  onGetResults,
  onCancel,
  token,
}: FundSPAdvancedCriteriaPopupProps) => {
  const [screen, setScreen] = useState<Screen>('home');

  const activeAssetType = assetTypes.find((item) => item.activated);
  const activeClassification = classifications.find((item) => item.activated);

  const handleResetAll = () => {
    setTempCriteria(tempCriteria.map((item) => ({ ...item, selected: false })));
    setAssetTypes(
      assetTypes.map((item) => ({ ...item, activated: item.value === 'ALL' })),
    );
    setClassifications(
      classifications.map((item) => ({
        ...item,
        activated: item.value === 'ALL',
      })),
    );
  };

  const hasPendingMinMax = tempCriteria.some(
    (item) => item.selected && (item.pendingMinMax || item.minMaxUnavailable),
  );

  const headerTitle: { [key in Screen]: string } = {
    home: 'Screener criteria',
    assetType: 'Select asset type',
    classification: 'Select classification',
    selectCriteria: 'Select criteria',
  };

  const handleBack = () => {
    if (screen === 'home') {
      setOpenSPAdvancedCriteria(false);
    } else {
      setScreen('home');
    }
  };

  const renderHome = () => (
    <Box className="fund-sp-criteria-screen">
      <Box className="fund-sp-criteria-screen-scroll">
        <Box className="fund-sp-criteria-field-group">
          <div className="fund-sp-criteria-field-title body-m-bold">
            Asset type
          </div>
          <Box
            component="button"
            className="fund-sp-criteria-field-dropdown"
            onClick={() => setScreen('assetType')}
          >
            <span className="fund-sp-criteria-field-dropdown-value body-m-regular">
              {activeAssetType && activeAssetType.value !== 'ALL'
                ? activeAssetType.label
                : 'All Asset types'}
            </span>
            <ArrowDownIcon className="fund-sp-criteria-field-dropdown-arrow" />
          </Box>
        </Box>
        <Box className="fund-sp-criteria-field-group">
          <div className="fund-sp-criteria-field-title body-m-bold">
            Classification
          </div>
          <Box
            component="button"
            className="fund-sp-criteria-field-dropdown"
            onClick={() => setScreen('classification')}
          >
            <span className="fund-sp-criteria-field-dropdown-value body-m-regular">
              {activeClassification && activeClassification.value !== 'ALL'
                ? activeClassification.label
                : 'All Classifications'}
            </span>
            <ArrowDownIcon className="fund-sp-criteria-field-dropdown-arrow" />
          </Box>
        </Box>

        <FundCriteriaDetailList
          market={market}
          tempCriteria={tempCriteria}
          selectedCriteria={tempCriteria.filter((item) => item.selected)}
          setCriteria={setTempCriteria}
          token={token}
        />
      </Box>

      <Box className="fund-sp-criteria-add-button-wrapper">
        <Button
          className="fund-sp-criteria-add-button"
          sx={addButtonSx}
          onClick={() => setScreen('selectCriteria')}
        >
          Add a criteria
        </Button>
      </Box>

      <Box className="fund-sp-criteria-footer">
        <Button
          className="fund-sp-criteria-primary-button"
          sx={{ ...primaryButtonSx, backgroundColor: themeColor }}
          disabled={hasPendingMinMax}
          onClick={onGetResults}
        >
          Get results
        </Button>
        <Button
          className="fund-sp-criteria-text-button"
          sx={textButtonSx}
          onClick={handleResetAll}
        >
          Reset all
        </Button>
        <Button
          className="fund-sp-criteria-text-button"
          sx={textButtonSx}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  const renderAssetType = () => (
    <Box className="fund-sp-criteria-screen">
      <Box className="fund-sp-criteria-screen-scroll">
        <SingleSelectCriterion
          allLabel="All Asset Types"
          items={assetTypes}
          setItems={setAssetTypes}
          variant="list"
        />
      </Box>
      <Box className="fund-sp-criteria-footer">
        <Button
          className="fund-sp-criteria-text-button"
          sx={textButtonSx}
          onClick={() => setScreen('home')}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  const renderClassification = () => (
    <Box className="fund-sp-criteria-screen">
      <Box className="fund-sp-criteria-screen-scroll">
        <SingleSelectCriterion
          allLabel="All Classifications"
          items={classifications}
          setItems={setClassifications}
          variant="list"
        />
      </Box>
      <Box className="fund-sp-criteria-footer">
        <Button
          className="fund-sp-criteria-text-button"
          sx={textButtonSx}
          onClick={() => setScreen('home')}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  const renderSelectCriteria = () => (
    <Box className="fund-sp-criteria-screen">
      <Box className="fund-sp-criteria-screen-scroll">
        <FundCriteriaSelect
          criteria={tempCriteria}
          setCriteria={setTempCriteria}
        />
      </Box>
      <Box className="fund-sp-criteria-footer">
        <Button
          className="fund-sp-criteria-primary-button"
          sx={{ ...primaryButtonSx, backgroundColor: themeColor }}
          onClick={() => setScreen('home')}
        >
          {`Add selected criteria (${tempCriteria.filter((item) => item.selected).length})`}
        </Button>
        <Button
          className="fund-sp-criteria-text-button"
          sx={textButtonSx}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );

  const renderScreen = () => {
    switch (screen) {
      case 'assetType':
        return renderAssetType();
      case 'classification':
        return renderClassification();
      case 'selectCriteria':
        return renderSelectCriteria();
      default:
        return renderHome();
    }
  };

  return (
    <Box className="fund-sp-advanced-criteria-popup">
      <Box className="fund-sp-advanced-criteria-popup-inner">
        <Box className="fund-sp-advanced-criteria-popup-header">
          <IconButton
            className="fund-sp-advanced-criteria-popup-back"
            sx={backButtonSx}
            aria-label="Back"
            onClick={handleBack}
          >
            <ArrowLeftIcon />
          </IconButton>
          <div className="body-m-bold fund-sp-advanced-criteria-popup-title">
            {headerTitle[screen]}
          </div>
        </Box>
        {renderScreen()}
      </Box>
    </Box>
  );
};

export default FundSPAdvancedCriteriaPopup;
